import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'productImage'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #pendingDrawerOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.stickyVariantSelect?.addEventListener('change', this.#handleStickyVariantChange, { signal });

    this.#getInitialQuantity();
    this.#updateStickyPriceFromVariant(this.#getSelectedStickyVariantSettings());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#pendingDrawerOpen = true;
    this.#syncMainQuantitySelector(this.#currentQuantity);
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    if (event.detail.data.clientOptimistic) {
      this.dataset.variantAvailable = `${Boolean(variant?.available)}`;

      if (variant?.id) {
        this.dataset.currentVariantId = String(variant.id);
      }
      this.#syncStickySelect(variant?.id);

      if (this.refs.productImage instanceof HTMLImageElement) {
        const productVariantMedia = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.productImage.src = productVariantMedia.startsWith('//') ? `https:${productVariantMedia}` : productVariantMedia;
        }
      }

      this.#updateButtonState(variant);
      this.#updateStickyPriceFromVariant(variant);
      return;
    }

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#updateButtonState(variant);
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    this.#updateButtonState(null);
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (this.#pendingDrawerOpen && _event.type === ThemeEvents.cartUpdate) {
      const cartDrawer = document.querySelector('cart-drawer-component');
      cartDrawer?.open?.();
    }

    this.#pendingDrawerOpen = false;
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    const source = event.target;
    const cameFromSticky = source instanceof HTMLElement && source.closest('sticky-add-to-cart') === this;

    if (cameFromSticky) {
      this.#syncMainQuantitySelector(event.detail.quantity);
    } else {
      this.#syncStickyQuantitySelector(event.detail.quantity);
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    const mainQuantityInput = this.mainQuantitySelector?.refs?.quantityInput;
    this.#currentQuantity = parseInt(mainQuantityInput?.value || this.dataset.initialQuantity || '1') || 1;
    this.#syncStickyQuantitySelector(this.#currentQuantity);
  }

  #handleStickyVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement | null} */ (event.target instanceof HTMLSelectElement ? event.target : null);
    if (!select) return;

    const selectedOption = select.selectedOptions[0];
    if (!(selectedOption instanceof HTMLOptionElement)) return;

    const variantSettings = this.#parseVariantSettings(selectedOption);
    if (!variantSettings) return;

    this.#syncMainVariantPicker(variantSettings);
  };

  #parseVariantSettings(option) {
    const { variantSettings } = option.dataset;
    if (!variantSettings) return null;

    try {
      return JSON.parse(variantSettings);
    } catch (_error) {
      return null;
    }
  }

  #getSelectedStickyVariantSettings() {
    const select = this.stickyVariantSelect;
    if (!select) return null;

    const selectedOption = select.selectedOptions[0];
    if (!(selectedOption instanceof HTMLOptionElement)) return null;

    return this.#parseVariantSettings(selectedOption);
  }

  #syncMainVariantPicker(variantSettings) {
    const variantPicker = this.mainVariantPicker;
    if (!variantPicker) return;

    const optionValues = [variantSettings.option1, variantSettings.option2, variantSettings.option3].filter(Boolean);
    const controls = Array.from(
      variantPicker.querySelectorAll('fieldset.variant-option, .variant-option--dropdowns select.variant-option__select')
    );

    let lastChangedTarget = null;

    controls.forEach((control, index) => {
      const value = optionValues[index];
      if (!value) return;

      if (control instanceof HTMLFieldSetElement) {
        const radio = Array.from(control.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        if (radio instanceof HTMLInputElement && !radio.checked) {
          variantPicker.updateSelectedOption(radio);
          lastChangedTarget = radio;
        }
      } else if (control instanceof HTMLSelectElement) {
        if (control.value !== value) {
          control.value = value;
          variantPicker.updateSelectedOption(control);
          lastChangedTarget = control;
        }
      }
    });

    if (lastChangedTarget instanceof HTMLElement) {
      lastChangedTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #updateButtonState(variant) {
    const button = this.refs.addToCartButton;
    const label = button.querySelector('[data-product-add-to-cart-text]');
    const available = Boolean(variant?.available);

    button.disabled = !available;

    if (label instanceof HTMLElement) {
      if (available) {
        label.textContent = this.dataset.labelAddToCart || 'Add to cart';
      } else if (variant) {
        label.textContent = this.dataset.labelSoldOut || 'Sold out';
      } else {
        label.textContent = this.dataset.labelUnavailable || 'Unavailable';
      }
    }
  }

  #updateStickyPriceFromVariant(variant) {
    if (!variant) return;

    const totalMoney = this.querySelector('.dbtfy-sticky-addtocart__price--total .money');
    const compareMoney = this.querySelector('.dbtfy-sticky-addtocart__price--compare .money');
    const compareWrapper = this.querySelector('.dbtfy-sticky-addtocart__price--compare');
    const discountWrapper = this.querySelector('.dbtfy-sticky-addtocart__discount-saved');
    const discountText = this.querySelector('.dbtfy-sticky-addtocart__discount-saved-text');

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const price = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || variant.comparePrice || 0);
    const hasDiscount = compareAtPrice > price;

    if (totalMoney instanceof HTMLElement) {
      totalMoney.textContent = formatMoney(price, moneyFormat, currency).replace(/<[^>]*>/g, '');
    }

    if (hasDiscount) {
      if (compareMoney instanceof HTMLElement) {
        compareMoney.textContent = formatMoney(compareAtPrice, moneyFormat, currency).replace(/<[^>]*>/g, '');
      }
      if (discountText instanceof HTMLElement) {
        discountText.textContent = `SAVE ${formatMoney(compareAtPrice - price, moneyFormat, currency).replace(/<[^>]*>/g, '')}`;
      }
      compareWrapper?.removeAttribute('hidden');
      discountWrapper?.removeAttribute('hidden');
    } else {
      compareWrapper?.setAttribute('hidden', 'hidden');
      discountWrapper?.setAttribute('hidden', 'hidden');
    }
  }

  #syncStickySelect(variantId) {
    const select = this.stickyVariantSelect;
    if (!select || !variantId) return;
    const option = Array.from(select.options).find((item) => item.value === String(variantId));
    if (option) {
      select.value = option.value;
    }
  }

  #syncMainQuantitySelector(quantity) {
    const quantitySelector = this.mainQuantitySelector;
    if (!quantitySelector) return;
    quantitySelector.setValue(String(quantity));
    quantitySelector.updateButtonStates?.();
  }

  #syncStickyQuantitySelector(quantity) {
    const quantitySelector = this.stickyQuantitySelector;
    if (!quantitySelector) return;
    quantitySelector.setValue(String(quantity));
    quantitySelector.updateButtonStates?.();
  }

  get stickyVariantSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('.dbtfy-sticky-addtocart__select'));
  }

  get stickyQuantitySelector() {
    return /** @type {any | null} */ (this.querySelector('quantity-selector-component.dbtfy-sticky-addtocart__quantity'));
  }

  get mainQuantitySelector() {
    return /** @type {any | null} */ (this.#getProductForm()?.querySelector('quantity-selector-component'));
  }

  get mainVariantPicker() {
    return /** @type {any | null} */ (this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`));
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
